var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Schema, MapSchema, ArraySchema, type, view } from "@colyseus/schema";
import { GroundItem, MineNode, GrassNode, PortalNode, WeedPlant } from "./items.js";
import { Hog, Goblin, Slime, Chest, Guard, CityNpc, PetHog } from "./mobs.js";
import { BlackjackTable } from "./blackjack.js";
import { PokerTable } from "./poker.js";
import { Appearance } from "./appearance.js";
/**
 * Authoritative per-player state synced by Colyseus.
 *
 * NOTE: this file is compiled with `tsc` (experimentalDecorators + emitDecoratorMetadata,
 * useDefineForClassFields=false). It must NOT be transformed by esbuild/Vite directly —
 * consumers import the built output in `dist/`.
 */
export class Player extends Schema {
    constructor() {
        super(...arguments);
        this.name = "";
        this.spriteId = 0;
        /** Layered paperdoll appearance (replaces spriteId; spriteId retained until migration completes). */
        this.appearance = new Appearance();
        this.x = 0;
        this.y = 0;
        /** Facing direction: one of the `Direction` values. */
        this.dir = "down";
        /** Whether the player is currently walking (drives remote walk animation). */
        this.moving = false;
        /** Whether the player is sprinting (drives remote run animation). */
        this.running = false;
        /** Casino seat the player is actively sitting at (0..NUM_SEATS-1), or -1. Drives the 3D
         *  "snap to the stool" rendering so everyone sees seated players at the table. */
        this.seatIndex = -1;
        /** Poker seat the player is actively sitting at (0..POKER_SEATS-1), or -1. Drives the 3D
         *  "snap to the stool" + seated sit pose so everyone sees seated players at the poker table. */
        this.pokerSeatIndex = -1;
        /** Equipped weapons (itemId or "") in the first/second hand. Only DRAWN in combat mode. */
        this.hand1 = "";
        /** Bait loaded in the EQUIPPED fishing rod (0 when hand1 isn't a rod). Synced so the owning
         *  client can gate casting without a round-trip. */
        this.rodBait = 0;
        this.hand2 = "";
        /** Item worn in the NECK accessory slot (itemId or ""). Non-visual; e.g. the Hog Charm. The worn
         *  charm's bound pet stats live server-side, not on this synced string. */
        this.neck = "";
        /** Rolled armored-leather bonus points on the WORN top/bottom, compact "s,d,a" ("" = none). The
         *  appearance can't carry them, so they're synced here so anyone inspecting (P) sees full gear stats. */
        this.topMods = "";
        this.bottomMods = "";
        /** COSMETIC garment overrides (itemId or "") per slot — a visual layer over the gear `appearance` that
         *  each client renders when cosmetics are shown. No stats; gear/stats live in `appearance` + mods. */
        this.cosHat = "";
        this.cosTop = "";
        this.cosBottom = "";
        this.cosShoes = "";
        /** Whether this player's cosmetics are shown (off ⇒ everyone sees the real gear). Forced off in Kapon
         *  + while working (the work uniform shows) — those are client-side, keyed on the viewer's zone. */
        this.showCosmetics = true;
        /** Whether the player is mid mining-channel — drives the swinging-pickaxe animation everyone
         *  (not just the miner) sees over their head. */
        this.mining = false;
        /** Whether the player is mid crafting-channel — drives the spinning-gear animation everyone
         *  (not just the crafter) sees over their head. */
        this.crafting = false;
        /** Whether the player is fishing (line cast) — drives the seated/casting pose everyone sees. */
        this.fishing = false;
        /** Fishing ROD stance everyone sees: 0 none · 1 held idle · 2 charging a cast · 3 line cast ·
         *  4 hooked/reeling. Client-driven (MessageType.FishState), rendered as the rod prop on remotes. */
        this.fishState = 0;
        // --- combat (synced to everyone so others see your health bar + stances) ---
        /** Live health + cap. 0 ⇒ downed (see `dead`). */
        this.health = 100;
        this.maxHealth = 100;
        /** Currently holding a block stance (raised shield) — drives the block animation + damage negation. */
        this.blocking = false;
        /** Mid attack swing — drives the attack animation (briefly true). */
        this.attacking = false;
        /** Weapon drawn (combat mode) — everyone shows the held sword/shield only while this is true. */
        this.combat = false;
        /** Downed: health hit 0, laying on the ground until the respawn timer elapses. */
        this.dead = false;
        /** Smoking a Blunt — replaces the idle with the standing-smoke loop for everyone (20s buff). */
        this.smoking = false;
        /** RESTING in a bed (furniture): snapped onto the bed in a sit pose, healing over time, until the
         *  player presses E to get up. Server-set (UseFurniture toggles it). */
        this.resting = false;
        /** BMX: 0 stowed (in bag) · 1 parked in the world (at bmxX/bmxY) · 2 riding. */
        this.bmxState = 0;
        this.bmxX = 0;
        this.bmxY = 0;
        /** Stunned (no actions, no movement) — briefly true after your attack is fully blocked. */
        this.stunned = false;
        /** Monotonic jump counter — bumped server-side each time the player presses jump. Remotes
         *  edge-detect the increment to play the hop + airborne pose (a transient action, not a state). */
        this.jumpSeq = 0;
        /** Clocked in to a job (Watabaga shift) — wears the red uniform + clothing slots locked. */
        this.working = false;
        /** PvP karma. 0 = neutral; < 0 ("red-named") ⇒ skeleton marker, hunted by guards, barred from
         *  the safe city. Drops on attacking/killing peaceful players, recovers over time, resets on death. */
        this.affinity = 0;
        /** Building storey the player stands on (0 = ground; 2/3 = the tenement's upper floors). The
         *  apartment homes physically stack inside the building at the same 2D footprint, so a synced
         *  floor is the only way remotes can be rendered at the right height. Client-authoritative,
         *  set when travelling through a floor door (like x/y). */
        this.floor = 0;
        /** This player's account is flagged admin (Ghost) — gates admin-only UI (e.g. start an auction). */
        this.admin = false;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "name", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "spriteId", void 0);
__decorate([
    type(Appearance),
    __metadata("design:type", Object)
], Player.prototype, "appearance", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "y", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "dir", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "moving", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "running", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "seatIndex", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "pokerSeatIndex", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "hand1", void 0);
__decorate([
    type("uint8"),
    __metadata("design:type", Object)
], Player.prototype, "rodBait", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "hand2", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "neck", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "topMods", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "bottomMods", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "cosHat", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "cosTop", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "cosBottom", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Player.prototype, "cosShoes", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "showCosmetics", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "mining", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "crafting", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "fishing", void 0);
__decorate([
    type("uint8"),
    __metadata("design:type", Object)
], Player.prototype, "fishState", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "health", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "maxHealth", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "blocking", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "attacking", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "combat", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "dead", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "smoking", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "resting", void 0);
__decorate([
    type("uint8"),
    __metadata("design:type", Object)
], Player.prototype, "bmxState", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "bmxX", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "bmxY", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "stunned", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "jumpSeq", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "working", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Player.prototype, "affinity", void 0);
__decorate([
    type("uint8"),
    __metadata("design:type", Object)
], Player.prototype, "floor", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Player.prototype, "admin", void 0);
/** A hired worker (character id + name) on a building's staff. */
export class Employee extends Schema {
    constructor() {
        super(...arguments);
        this.id = "";
        this.name = "";
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Employee.prototype, "id", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Employee.prototype, "name", void 0);
/** A furniture piece placed inside a building (bed, safebox, …), synced so every client renders it.
 *  x/y are server px (the same 2D space as players); z is the height above the room floor in world
 *  units (apartment homes add their storey's elevation client-side). A safebox's stored items stay
 *  PRIVATE server-side (like the fridge) — only this placement record is public. */
export class PlacedFurniture extends Schema {
    constructor() {
        super(...arguments);
        this.fid = ""; // stable placement id (unique per building)
        this.itemId = ""; // "bed" | "safebox" | …
        this.x = 0;
        this.y = 0;
        this.z = 0;
        /** Yaw rotation in radians (0..2π), set in the placement mode's ROTATE step. */
        this.rot = 0;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PlacedFurniture.prototype, "fid", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PlacedFurniture.prototype, "itemId", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PlacedFurniture.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PlacedFurniture.prototype, "y", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PlacedFurniture.prototype, "z", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PlacedFurniture.prototype, "rot", void 0);
/** An ownable building's live state, synced to everyone. Keyed by building id in GameState.buildings.
 *  Drives the door lock + owner/employee entry + the owner-control panel. Mirror of the Building doc. */
export class Building extends Schema {
    constructor() {
        super(...arguments);
        this.ownerId = ""; // owning character id ("" = unowned)
        this.ownerName = ""; // owner's display name (for plates/prompts)
        this.locked = false; // door locked — only owner/employees may enter
        this.employees = new ArraySchema(); // hired staff (id + name)
        this.applicants = new ArraySchema(); // pending job applicants (id + name)
        this.forSale = false; // owner listed it for sale (still theirs until someone buys)
        this.salePrice = 0; // the owner's asking price while forSale
        /** Door-KEY holders (houses only): may enter while locked + lock/unlock. Given/revoked by the owner. */
        this.keyholders = new ArraySchema();
        /** Houses: window curtains drawn CLOSED (blocks the view both ways). Toggled at the window (E). */
        this.curtains = false;
        // ── Live auction (admin-started). endsAt > 0 ⇒ auction running; all money moves via the BANK. ──
        /** Epoch ms when the auction closes (0 = no auction). */
        this.auctionEndsAt = 0;
        /** Opening price — the minimum first bid. */
        this.auctionStartPrice = 0;
        /** Minimum raise over the current bid. */
        this.auctionMinIncrement = 0;
        /** Current highest bid (0 = no bids yet). The bid amount is HELD from the bidder's bank. */
        this.auctionBid = 0;
        /** Current highest bidder (character id + display name; "" = no bids yet). */
        this.auctionBidderId = "";
        this.auctionBidderName = "";
        /** Furniture placed inside this building (owner/keyholder-placed; everyone renders it). */
        this.furniture = new ArraySchema();
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Building.prototype, "ownerId", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Building.prototype, "ownerName", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Building.prototype, "locked", void 0);
__decorate([
    type([Employee]),
    __metadata("design:type", Object)
], Building.prototype, "employees", void 0);
__decorate([
    type([Employee]),
    __metadata("design:type", Object)
], Building.prototype, "applicants", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Building.prototype, "forSale", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Building.prototype, "salePrice", void 0);
__decorate([
    type([Employee]),
    __metadata("design:type", Object)
], Building.prototype, "keyholders", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], Building.prototype, "curtains", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Building.prototype, "auctionEndsAt", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Building.prototype, "auctionStartPrice", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Building.prototype, "auctionMinIncrement", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], Building.prototype, "auctionBid", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Building.prototype, "auctionBidderId", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], Building.prototype, "auctionBidderName", void 0);
__decorate([
    type([PlacedFurniture]),
    __metadata("design:type", Object)
], Building.prototype, "furniture", void 0);
export class GameState extends Schema {
    constructor() {
        super(...arguments);
        // INTEREST FILTERING: every region-scoped collection is @view()-tagged, so a client only receives the
        // entities the server adds to ITS StateView (the ones in the player's current region — see GameRoom's
        // reconcileViews). The single casino tables + buildings stay GLOBAL (un-tagged): tiny + needed map-wide.
        this.players = new MapSchema();
        /** Shared pickable items in the world, keyed by a stable id (e.g. "g0"). */
        this.groundItems = new MapSchema();
        /** Shared mineable rock nodes in the city's mine area, keyed by id (e.g. "m0"). */
        this.mineNodes = new MapSchema();
        /** Shared cuttable grass tufts in the city, keyed by id (e.g. "gr0"). */
        this.grassNodes = new MapSchema();
        /** Player-planted weed plants growing in the Kapon wilds, keyed by id (e.g. "wp0"). */
        this.plants = new MapSchema();
        /** Shared timed teleport portals (opened by a scroll/host), keyed by id (e.g. "p0"). */
        this.portals = new MapSchema();
        /** Server-driven attack NPCs (wild hogs) roaming the Kapon wilds, keyed by id (e.g. "hog0"). */
        this.hogs = new MapSchema();
        /** Server-driven wild goblins (combat lvl 10) roaming the Kapon wilds, keyed by id (e.g. "goblin0"). */
        this.goblins = new MapSchema();
        /** Server-driven wild slimes (combat lvl 15) roaming the Kapon wilds, keyed by id (e.g. "slime0"). */
        this.slimes = new MapSchema();
        /** Summoned pet hogs (mini companions), keyed by the OWNER's sessionId (one per player). */
        this.petHogs = new MapSchema();
        /** Friendly gate guards (combat NPCs) holding the Kapon gate, keyed by id (e.g. "guard0"). */
        this.guards = new MapSchema();
        /** Loot chests dropped by slain mobs (10% from a hog), keyed by id (e.g. "chest0"). */
        this.chests = new MapSchema();
        /** Server-simulated city NPCs (walk-in customers; later wandering townsfolk), keyed by id ("npc0"). */
        this.cityNpcs = new MapSchema();
        /** The shared blackjack table (single table in v1). */
        this.blackjack = new BlackjackTable();
        /** The shared Texas Hold'em poker table (single table). */
        this.poker = new PokerTable();
        /** Ownable buildings keyed by building id (OWNABLE_BUILDINGS). Owner + lock + employees, synced so
         *  every client agrees on who owns what + which doors are locked. Replaces the v1 `tavernLocked`. */
        this.buildings = new MapSchema();
    }
}
__decorate([
    view(),
    type({ map: Player }),
    __metadata("design:type", Object)
], GameState.prototype, "players", void 0);
__decorate([
    view(),
    type({ map: GroundItem }),
    __metadata("design:type", Object)
], GameState.prototype, "groundItems", void 0);
__decorate([
    view(),
    type({ map: MineNode }),
    __metadata("design:type", Object)
], GameState.prototype, "mineNodes", void 0);
__decorate([
    view(),
    type({ map: GrassNode }),
    __metadata("design:type", Object)
], GameState.prototype, "grassNodes", void 0);
__decorate([
    view(),
    type({ map: WeedPlant }),
    __metadata("design:type", Object)
], GameState.prototype, "plants", void 0);
__decorate([
    view(),
    type({ map: PortalNode }),
    __metadata("design:type", Object)
], GameState.prototype, "portals", void 0);
__decorate([
    view(),
    type({ map: Hog }),
    __metadata("design:type", Object)
], GameState.prototype, "hogs", void 0);
__decorate([
    view(),
    type({ map: Goblin }),
    __metadata("design:type", Object)
], GameState.prototype, "goblins", void 0);
__decorate([
    view(),
    type({ map: Slime }),
    __metadata("design:type", Object)
], GameState.prototype, "slimes", void 0);
__decorate([
    view(),
    type({ map: PetHog }),
    __metadata("design:type", Object)
], GameState.prototype, "petHogs", void 0);
__decorate([
    view(),
    type({ map: Guard }),
    __metadata("design:type", Object)
], GameState.prototype, "guards", void 0);
__decorate([
    view(),
    type({ map: Chest }),
    __metadata("design:type", Object)
], GameState.prototype, "chests", void 0);
__decorate([
    view(),
    type({ map: CityNpc }),
    __metadata("design:type", Object)
], GameState.prototype, "cityNpcs", void 0);
__decorate([
    type(BlackjackTable),
    __metadata("design:type", Object)
], GameState.prototype, "blackjack", void 0);
__decorate([
    type(PokerTable),
    __metadata("design:type", Object)
], GameState.prototype, "poker", void 0);
__decorate([
    type({ map: Building }),
    __metadata("design:type", Object)
], GameState.prototype, "buildings", void 0);
//# sourceMappingURL=state.js.map